import type { IconName } from '../components/Icon.js';

/**
 * How a venue renders.
 *
 * calendar-service stores one free-text `location` — whatever the employer
 * typed. There is no venue *kind* upstream to read, and guessing one (Zoom vs
 * Meet vs an address vs a room number) would be a taxonomy we invent and then
 * have to keep right. So: exactly one question is asked of the string — is it
 * something you can click? — and the answer decides the glyph.
 */
export interface Venue {
  kind: 'link' | 'place' | 'chat';
  /** What to show. For a link this is the URL itself: it is what was typed. */
  label: string;
  href?: string;
  icon: IconName;
}

/**
 * An empty venue is not missing data. It means the chat these two already have,
 * which is where an interview with nothing else booked happens.
 */
const IN_CHAT = 'In this chat';

export function readVenue(location: string): Venue {
  const text = location.trim();
  if (!text) return { kind: 'chat', label: IN_CHAT, icon: 'chatBubble' };

  // Anchored, so "Ask for https access at reception" stays a place. Nothing
  // clever beyond the scheme — a bare "meet.example.com" is not made clickable,
  // because guessing a scheme is how you send somebody to http:// in 2026.
  if (/^https?:\/\//i.test(text)) {
    return { kind: 'link', label: text, href: text, icon: 'video' };
  }

  return { kind: 'place', label: text, icon: 'mapPin' };
}
